/**
 * "Yevmiye mi, PAJE mi? Hangisini kaydetmeliyim?" — shared posting-guidance
 * explainer mounted on BOTH the Journal Entries (Yevmiye) and PAJE tabs.
 *
 * Clarifies the single most common point of confusion: the two outputs are
 * ALTERNATIVES, not additive.
 *   • Journal Entries = the complete IFRS 16 ledger for the lease (initial
 *     recognition → interest/depreciation/payment → FX revaluation → mods).
 *   • PAJE            = the auditor's adjustment = (correct IFRS 16) − (what
 *     the client already booked).
 * Posting both for the same lease double-counts.
 *
 * Content lives entirely in i18n (`posting.*`); `id` differentiates the two
 * mount points (e.g. "je-posting" / "paje-posting") so the ExplainerPanel
 * test-ids and Accordion state stay independent.
 */

import { useTranslation } from "react-i18next";

import { ExplainerPanel } from "@/components/common/ExplainerPanel";

export interface PostingGuidancePanelProps {
  /** Unique mount id → ExplainerPanel test-ids `${id}-guide(-trigger)`. */
  id: string;
}

export function PostingGuidancePanel({ id }: PostingGuidancePanelProps) {
  const { t } = useTranslation();
  return (
    <ExplainerPanel id={id} title={t("posting.title")}>
      <p className="text-muted-foreground">{t("posting.intro")}</p>

      <section className="space-y-1">
        <p className="font-medium text-foreground">
          {t("posting.scenario1Title")}
        </p>
        <p className="text-muted-foreground">{t("posting.scenario1Body")}</p>
      </section>

      <section className="space-y-1">
        <p className="font-medium text-foreground">
          {t("posting.scenario2Title")}
        </p>
        <p className="text-muted-foreground">{t("posting.scenario2Body")}</p>
      </section>

      <section className="space-y-1 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
        <p className="font-medium text-foreground">{t("posting.ruleTitle")}</p>
        <p className="text-muted-foreground">{t("posting.ruleBody")}</p>
      </section>

      <p className="text-xs text-muted-foreground">{t("posting.fxNote")}</p>
      <p className="text-xs italic text-muted-foreground">
        {t("posting.disclaimer")}
      </p>
    </ExplainerPanel>
  );
}
